/**
 * AssetAttachments · Feb 2026
 *
 * Image / video gallery for an Asset, backed by the universal
 * /attachments endpoints. Each attachment carries a human title +
 * description (editable in-place).
 *
 * Constraints:
 *   - Backend enforces image/* or video/* MIME only for scope=asset.
 *   - 25 MB per file.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Paperclip, Trash2, Save, Image as ImageIcon, Video, Upload, Edit3, X } from "lucide-react";

const AXIOS_BASE = api.defaults?.baseURL || "";

export default function AssetAttachments({ assetId, canManage = true }) {
  const [rows, setRows] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [editing, setEditing] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/attachments/asset/${assetId}`);
      setRows(data || []);
    } catch { setRows([]); }
  }, [assetId]);
  useEffect(() => { load(); }, [load]);

  const onPick = () => inputRef.current?.click();

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset so re-picking the same file works
    if (!files.length) return;
    for (const file of files) {
      if (!/^image\/|^video\//.test(file.type)) {
        toast.error(`${file.name} — only images or videos allowed`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (title) fd.append("title", title);
        if (description) fd.append("description", description);
        await api.post(`/attachments/asset/${assetId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Uploaded ${file.name}`);
      } catch (err) { toast.error(apiErrorMessage(err, "Upload failed")); }
      finally { setUploading(false); }
    }
    setTitle(""); setDescription("");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/attachments/${id}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const saveEdits = async () => {
    try {
      await api.patch(`/attachments/${editing.id}`, {
        title: editing.title || null,
        description: editing.description || null,
      });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const dlUrl = (a) => `${AXIOS_BASE}/attachments/file/${a.id}/download`;
  const isImg = (a) => (a.content_type || "").startsWith("image/");
  const isVid = (a) => (a.content_type || "").startsWith("video/");

  return (
    <div className="mt-5 border-t border-border pt-4" data-testid="asset-attachments">
      <div className="flex items-center justify-between mb-3">
        <h4 className="label-eyebrow flex items-center gap-2">
          <Paperclip className="w-3.5 h-3.5" />
          Attachments ({rows?.length ?? 0})
        </h4>
        {canManage && (
          <div className="flex items-center gap-2">
            <Input placeholder="Title (optional)" value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="h-8 w-40 text-xs rounded-none"
                    data-testid="ast-attach-title" />
            <Input placeholder="Description (optional)" value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="h-8 w-56 text-xs rounded-none"
                    data-testid="ast-attach-desc" />
            <Button size="sm" onClick={onPick} disabled={uploading}
                     className="rounded-none" data-testid="ast-attach-upload">
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {uploading ? "Uploading…" : "Add media"}
            </Button>
            <input ref={inputRef} type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={onFilesChosen}
                    className="hidden"
                    data-testid="ast-attach-file-input" />
          </div>
        )}
      </div>

      {rows === null ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs italic text-muted-foreground border border-dashed border-border p-4 text-center"
             data-testid="ast-attach-empty">
          No media yet. Upload photos or videos to keep a visual record of this asset.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3"
             data-testid="ast-attach-grid">
          {rows.map((a) => (
            <div key={a.id} className="border border-border rounded-none bg-background overflow-hidden group"
                 data-testid={`ast-attach-${a.id}`}>
              <div className="aspect-video bg-muted flex items-center justify-center relative">
                {isImg(a) && (
                  <img src={dlUrl(a)} alt={a.title || a.filename}
                       className="w-full h-full object-cover"
                       data-testid={`ast-attach-img-${a.id}`} />
                )}
                {isVid(a) && (
                  <video src={dlUrl(a)} controls preload="metadata"
                          className="w-full h-full object-cover"
                          data-testid={`ast-attach-vid-${a.id}`} />
                )}
                {!isImg(a) && !isVid(a) && (
                  <ImageIcon className="w-8 h-8 text-muted-foreground" />
                )}
                {canManage && (
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button type="button"
                             onClick={() => setEditing({ id: a.id, title: a.title || "", description: a.description || "" })}
                             className="bg-background/90 hover:bg-background px-1.5 py-1 border border-border"
                             title="Edit caption"
                             data-testid={`ast-attach-edit-${a.id}`}>
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button type="button" onClick={() => del(a.id)}
                             className="bg-background/90 hover:bg-destructive hover:text-destructive-foreground px-1.5 py-1 border border-border"
                             title="Delete"
                             data-testid={`ast-attach-del-${a.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {isVid(a) && (
                  <div className="absolute top-1 left-1 bg-background/80 border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                    <Video className="w-3 h-3 inline mr-1" />Video
                  </div>
                )}
              </div>
              <div className="p-2 text-xs">
                <div className="font-medium truncate" title={a.title || a.filename}>
                  {a.title || a.filename}
                </div>
                {a.description && (
                  <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5"
                       title={a.description}>
                    {a.description}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground/70 mt-1">
                  {a.uploader_name || "—"} · {(a.created_at || "").slice(0, 10)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditCaptionDialog
          value={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={saveEdits}
        />
      )}
    </div>
  );
}

function EditCaptionDialog({ value, onChange, onCancel, onSave }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         onClick={onCancel} data-testid="ast-attach-edit-dialog"
         role="dialog" aria-labelledby="ast-attach-edit-title-h">
      <div className="bg-background border border-border max-w-md w-full p-4 space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h4 id="ast-attach-edit-title-h" className="font-semibold text-sm">Edit caption</h4>
          <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Title</label>
          <Input value={value.title}
                  onChange={(e) => onChange({ ...value, title: e.target.value })}
                  className="mt-1 rounded-none"
                  data-testid="ast-attach-edit-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</label>
          <Textarea value={value.description}
                     onChange={(e) => onChange({ ...value, description: e.target.value })}
                     rows={3}
                     className="mt-1 rounded-none text-sm"
                     data-testid="ast-attach-edit-desc" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} className="rounded-none">Cancel</Button>
          <Button onClick={onSave} className="rounded-none" data-testid="ast-attach-edit-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> Save
          </Button>
        </div>
      </div>
    </div>
  );
}
